import React, { useState, useCallback, useEffect, useRef } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  MarkerType,
  getBezierPath,
  BaseEdge,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { ArrowLeft, Save, Play, Zap, GitBranch, Send, Mail, Sparkles, Plus, Target, X, CheckCircle, XCircle, AlertCircle, Users, CheckSquare, Tag, ChevronDown, ChevronRight, Search, Trash2 } from 'lucide-react';
import api from '../utils/api';
import TriggerNode from './workflow-nodes/TriggerNode';
import ConditionNode from './workflow-nodes/ConditionNode';
import IfElseNode from './workflow-nodes/IfElseNode';
import ActionNode from './workflow-nodes/ActionNode';
import NodeConfigModal from './workflow-nodes/NodeConfigModal';

// Custom edge with delete button
const DeletableEdge = ({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, style = {}, markerEnd, data }) => {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const onEdgeDelete = (evt) => {
    evt.stopPropagation();
    if (data?.onDelete) {
      data.onDelete(id);
    }
  };

  return (
    <>
      <BaseEdge path={edgePath} markerEnd={markerEnd} style={style} />
      <foreignObject
        width={24}
        height={24}
        x={labelX - 12}
        y={labelY - 12}
        className="edgebutton-foreignobject"
        requiredExtensions="http://www.w3.org/1999/xhtml"
      >
        <div className="flex items-center justify-center w-full h-full">
          <button
            className="w-5 h-5 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white shadow-md transition-all opacity-70 hover:opacity-100"
            onClick={onEdgeDelete}
            title="Delete connection"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </foreignObject>
    </>
  );
};

const edgeTypes = {
  deletable: DeletableEdge,
};

const nodeTypes = {
  trigger: TriggerNode,
  condition: ConditionNode,
  ifelse: IfElseNode,
  action: ActionNode,
};

const WorkflowBuilder = ({ workflow, onClose, user }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [workflowName, setWorkflowName] = useState('');
  const [workflowDescription, setWorkflowDescription] = useState('');
  const [triggerType, setTriggerType] = useState('task_stage_changed');
  const [triggerConfig, setTriggerConfig] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showNodePalette, setShowNodePalette] = useState(true);
  const [selectedNodeForConfig, setSelectedNodeForConfig] = useState(null);
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testTasks, setTestTasks] = useState([]);
  const [testClients, setTestClients] = useState([]);
  const [testUsers, setTestUsers] = useState([]);
  const [selectedTestTask, setSelectedTestTask] = useState('');
  const [selectedTestClient, setSelectedTestClient] = useState('');
  const [selectedTestUser, setSelectedTestUser] = useState('');
  const [testResults, setTestResults] = useState(null);
  const [testing, setTesting] = useState(false);
  const [showRawTestLog, setShowRawTestLog] = useState(false);
  const [availableWorkflows, setAvailableWorkflows] = useState([]);
  const [saveToast, setSaveToast] = useState(null); // { type: 'success' | 'error', message: string }
  // Track workflow ID locally (for newly created workflows)
  const [currentWorkflowId, setCurrentWorkflowId] = useState(workflow?.id || null);

  const normalizeActionNode = (node) => {
    if (node?.type !== 'action') return node;
    const actionType = node?.data?.actionType;
    const actionConfig = node?.data?.actionConfig || {};
    const withDefaults = (defaults) => ({ ...defaults, ...actionConfig });

    let normalizedActionConfig = actionConfig;

    switch (actionType) {
      case 'update_assignees':
        normalizedActionConfig = withDefaults({
          assignee_action: 'add',
          assignee_source: 'specific_users'
        });
        break;
      case 'email':
        normalizedActionConfig = withDefaults({
          send_from: 'system',
          recipient_type: 'assignee',
          from_email_account: 'primary',
          fallback_email: 'account_owner'
        });
        break;
      case 'ai_generate':
        normalizedActionConfig = withDefaults({
          content_type: 'email',
          audience: 'client'
        });
        break;
      case 'wait':
        normalizedActionConfig = withDefaults({
          duration: 5,
          unit: 'minutes'
        });
        break;
      case 'set_due_date':
        normalizedActionConfig = withDefaults({
          date_type: 'offset',
          days_offset: 7
        });
        break;
      case 'send_notification':
        normalizedActionConfig = withDefaults({
          notify_type: 'assignees'
        });
        break;
      case 'update_project_manager':
        normalizedActionConfig = withDefaults({
          pm_selection: 'specific'
        });
        break;
      case 'create_task':
        normalizedActionConfig = withDefaults({
          project_source: 'same',
          priority: 'normal',
          assignee_type: 'none',
          stage_type: 'first',
          due_date_type: 'none',
          estimated_effort_unit: 'hours',
          is_milestone: false,
          show_in_portal: false
        });
        break;
      case 'add_subtask':
        normalizedActionConfig = withDefaults({
          status: 'To Do',
          priority: 'Normal',
          assignee_type: 'none',
          due_date_type: 'none',
          estimated_effort_unit: 'hours'
        });
        break;
      case 'create_project':
        normalizedActionConfig = withDefaults({
          client_source: 'from_trigger',
          category: 'General',
          complexity: 'Medium',
          estimated_duration_metric: 'days',
          due_date_type: 'none',
          project_manager_type: 'none',
          create_default_tasks: true,
          create_primary_contact: true,
          create_general_project: true
        });
        break;
      case 'archive_task':
        normalizedActionConfig = withDefaults({
          archive_action: 'archive',
          target_task: 'trigger_task'
        });
        break;
      case 'grant_portal_access':
        normalizedActionConfig = withDefaults({
          contact_selection: 'primary_contact'
        });
        break;
      case 'send_portal_invite':
        normalizedActionConfig = withDefaults({
          contact_source: 'trigger_contact',
          send_verification_email: true
        });
        break;
      case 'gpt_action':
        normalizedActionConfig = withDefaults({
          model: 'gpt-4o',
          max_tokens: 500
        });
        break;
      default:
        break;
    }

    if (normalizedActionConfig === actionConfig) {
      return node;
    }

    return {
      ...node,
      data: {
        ...node.data,
        actionConfig: normalizedActionConfig
      }
    };
  };
  
  // Collapsible sections state
  const [expandedSections, setExpandedSections] = useState({
    triggers: true,
    logic: true,
    actions: true
  });
  const [actionSearch, setActionSearch] = useState('');
  const [webhookPayloadVariables, setWebhookPayloadVariables] = useState([]);

  // Fetch available workflows for "Add to Workflow" action
  useEffect(() => {
    const fetchWorkflows = async () => {
      try {
        const response = await api.get('/workflow-automations');
        const workflows = response.data.workflows || [];
        setAvailableWorkflows(workflows);
        // Also set on window for NodeConfigModal access
        window.__athena_workflows = workflows;
      } catch (err) {
        console.error('Error fetching workflows:', err);
      }
    };
    fetchWorkflows();
  }, []);

  // Load existing workflow or reset for new
  useEffect(() => {
    if (workflow) {
      console.log('📂 Loading existing workflow:', workflow.id, workflow.name);
      setWorkflowName(workflow.name || '');
      setWorkflowDescription(workflow.description || '');
      setTriggerType(workflow.trigger_type || 'task_stage_changed');
      setTriggerConfig(workflow.trigger_config || {});
      
      if (workflow.nodes && Array.isArray(workflow.nodes)) {
        // Store nodes initially - callbacks will be attached in separate useEffect
        setNodes(workflow.nodes);
      } else {
        setNodes([]);
      }
      if (workflow.edges && Array.isArray(workflow.edges)) {
        setEdges(workflow.edges);
      } else {
        setEdges([]);
      }
    } else {
      // Reset for new workflow
      console.log('📝 Creating new workflow - resetting state');
      setWorkflowName('');
      setWorkflowDescription('');
      setTriggerType('task_stage_changed');
      setTriggerConfig({});
      setNodes([]);
      setEdges([]);
    }
  }, [workflow, setNodes, setEdges]);

  const addDefaultTriggerNode = () => {
    const triggerNode = {
      id: 'trigger-1',
      type: 'trigger',
      position: { x: 250, y: 50 },
      data: {
        label: 'Trigger',
        triggerType: 'task_stage_changed',
        triggerConfig: {},
        onConfigClick: () => handleNodeConfig('trigger-1')
      },
    };
    setNodes([triggerNode]);
  };

  const handleNodeConfig = (nodeId) => {
    const node = nodes.find(n => n.id === nodeId);
    if (node) {
      setSelectedNodeForConfig(node);
    }
  };

  const handleSaveNodeConfig = (updatedNode) => {
    // Create the final node with callbacks attached
    const finalNode = {
      ...updatedNode,
      data: {
        ...updatedNode.data,
        onConfigClick: () => handleNodeConfig(updatedNode.id),
        onDelete: () => handleDeleteNode(updatedNode.id),
        onCopy: () => handleCopyNode(updatedNode.id)
      }
    };
    
    // Update nodes array
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === updatedNode.id) {
          return finalNode;
        }
        return node;
      })
    );
    
    // CRITICAL: Update selectedNodeForConfig to point to the updated node
    // This prevents useEffect from reloading stale data and clearing the config
    setSelectedNodeForConfig(finalNode);
  };

  // Handle double-click on node
  const onNodeDoubleClick = useCallback((event, node) => {
    handleNodeConfig(node.id);
  }, [nodes]);

  // Handle node deletion
  const handleDeleteNode = (nodeId) => {
    setNodes((nds) => nds.filter((node) => node.id !== nodeId));
    setEdges((eds) => eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId));
  };

  // Handle node copy
  const handleCopyNode = (nodeId) => {
    const nodeToCopy = nodes.find(n => n.id === nodeId);
    if (!nodeToCopy) return;

    // Deep-clone node settings and strip callback functions so all config is preserved
    const { onConfigClick, onDelete, onCopy, ...sourceData } = nodeToCopy.data || {};
    let copiedData;
    try {
      copiedData = (typeof structuredClone === 'function')
        ? structuredClone(sourceData)
        : JSON.parse(JSON.stringify(sourceData));
    } catch {
      copiedData = { ...sourceData };
    }

    const newNodeId = `${nodeToCopy.type}-${Date.now()}`;
    const newNode = {
      id: newNodeId,
      type: nodeToCopy.type,
      position: {
        x: nodeToCopy.position.x + 50,
        y: nodeToCopy.position.y + 50
      },
      data: {
        ...copiedData,
        label: `${copiedData.label || nodeToCopy.type} (Copy)`,
        onConfigClick: () => handleNodeConfig(newNodeId),
        onDelete: () => handleDeleteNode(newNodeId),
        onCopy: () => handleCopyNode(newNodeId)
      },
    };

    setNodes((nds) => nds.concat(newNode));
  };

  // Ensure all nodes have callbacks attached (important for loaded workflows)
  useEffect(() => {
    setNodes((currentNodes) => {
      // Check if any node is missing callbacks
      const needsUpdate = currentNodes.some(node => !node.data?.onDelete);
      if (!needsUpdate || currentNodes.length === 0) return currentNodes;
      
      console.log('🔧 Attaching callbacks to loaded nodes');
      return currentNodes.map(node => {
        // Skip if callbacks already exist
        if (node.data?.onDelete) return node;
        
        return {
          ...node,
          data: {
            ...node.data,
            onConfigClick: () => handleNodeConfig(node.id),
            onDelete: () => handleDeleteNode(node.id),
            onCopy: () => handleCopyNode(node.id)
          }
        };
      });
    });
  }, [nodes.length]); // Only run when nodes array length changes (e.g., after load)

  // Ensure all edges have delete handler and correct type (important for loaded workflows)
  useEffect(() => {
    setEdges((currentEdges) => {
      // Check if any edge is missing the delete handler or type
      const needsUpdate = currentEdges.some(edge => !edge.data?.onDelete || edge.type !== 'deletable');
      if (!needsUpdate || currentEdges.length === 0) return currentEdges;
      
      console.log('🔧 Attaching delete handler to loaded edges');
      return currentEdges.map(edge => ({
        ...edge,
        type: 'deletable',
        data: { 
          ...edge.data,
          onDelete: (edgeId) => setEdges((eds) => eds.filter((e) => e.id !== edgeId))
        }
      }));
    });
  }, [edges.length, setEdges]); // Run when edges array length changes

  // Handle key press for delete
  const onNodesDelete = useCallback((deleted) => {
    setEdges((eds) =>
      eds.filter((edge) => 
        !deleted.some((node) => node.id === edge.source || node.id === edge.target)
      )
    );
  }, [setEdges]);

  // onSelectionChange removed - not needed for icon-based copy

  // Old keyboard copy/paste removed - now using icon-based copy

  // Handle edge deletion
  const handleDeleteEdge = useCallback((edgeId) => {
    setEdges((eds) => eds.filter((e) => e.id !== edgeId));
  }, [setEdges]);

  const onConnect = useCallback(
    (params) =>
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            type: 'deletable',
            markerEnd: {
              type: MarkerType.ArrowClosed,
            },
            style: { stroke: '#9333ea', strokeWidth: 2 },
            data: { onDelete: handleDeleteEdge },
          },
          eds
        )
      ),
    [setEdges, handleDeleteEdge]
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      if (!reactFlowInstance || !reactFlowWrapper.current) return;

      const type = event.dataTransfer.getData('application/reactflow');
      const nodeData = JSON.parse(event.dataTransfer.getData('application/nodedata') || '{}');

      if (typeof type === 'undefined' || !type) {
        return;
      }

      // Get the bounding rect of the React Flow wrapper
      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      
      // Get current viewport (pan and zoom)
      const viewport = reactFlowInstance.getViewport();
      const zoom = viewport?.zoom || 1;
      const panX = viewport?.x || 0;
      const panY = viewport?.y || 0;

      // Calculate the mouse position relative to the React Flow container
      const mouseX = event.clientX - reactFlowBounds.left;
      const mouseY = event.clientY - reactFlowBounds.top;

      // Convert screen coordinates to flow coordinates
      // Formula: flowPos = (screenPos - pan) / zoom
      const flowX = (mouseX - panX) / zoom;
      const flowY = (mouseY - panY) / zoom;

      // Offset to center the node on the drop point
      const nodeWidth = type === 'ifelse' ? 220 : 200;
      const nodeHeight = type === 'ifelse' ? 80 : 60;

      const position = {
        x: flowX - nodeWidth / 2,
        y: flowY - nodeHeight / 2,
      };

      const newNodeId = `${type}-${Date.now()}`;
      const newNode = {
        id: newNodeId,
        type,
        position,
        data: {
          label: nodeData.label || type,
          ...nodeData,
          onConfigClick: () => handleNodeConfig(newNodeId),
          onDelete: () => handleDeleteNode(newNodeId),
          onCopy: () => handleCopyNode(newNodeId)
        },
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [reactFlowInstance, setNodes, handleNodeConfig]
  );

  // Fetch tasks and clients for testing
  const fetchTestData = async () => {
    try {
      // Fetch recent tasks
      const tasksResponse = await api.get('/tasks/hub');
      if (tasksResponse.data?.tasks) {
        setTestTasks(tasksResponse.data.tasks.slice(0, 20)); // Get first 20 tasks
      }
      
      // Fetch clients
      const clientsResponse = await api.get('/clients');
      if (clientsResponse.data) {
        setTestClients(clientsResponse.data.slice(0, 20)); // Get first 20 clients
      }
      
      // Fetch team users
      const usersResponse = await api.get('/team/members');
      if (usersResponse.data) {
        setTestUsers(usersResponse.data);
      }
    } catch (error) {
      console.error('Error fetching test data:', error);
    }
  };

  // Test workflow with selected data
  const runTestWorkflow = async () => {
    if (!workflow?.id) {
      alert('Please save the workflow first before testing');
      return;
    }

    if (!selectedTestTask) {
      alert('Please select a task to test with');
      return;
    }

    setTesting(true);
    setTestResults(null);

    try {
      const selectedTask = testTasks.find(t => t.id === selectedTestTask);
      
      // Build test data payload
      const testData = {
        task: {
          id: selectedTask.id,
          name: selectedTask.name,
          status: selectedTask.status,
          description: selectedTask.description || 'No description',
          priority: selectedTask.priority || 'medium',
          category: selectedTask.category_name || selectedTask.category || 'General',
          due_date: selectedTask.due_date,
          assignees: selectedTask.assignees || []
        }
      };

      // If client selected, add client info
      if (selectedTestClient) {
        const selectedClient = testClients.find(c => c.id === selectedTestClient);
        testData.client = {
          id: selectedClient.id,
          name: selectedClient.name,
          email: selectedClient.email
        };
      }

      // If user selected, add user info
      if (selectedTestUser) {
        const selectedUser = testUsers.find(u => u.id === selectedTestUser);
        testData.user = {
          id: selectedUser.id,
          name: selectedUser.name,
          email: selectedUser.email
        };
      }

      const response = await api.post(`/workflow-automations/${workflow.id}/test`, testData);

      setTestResults(response.data);
    } catch (error) {
      console.error('Test workflow error:', error);
      setTestResults({
        success: false,
        error: error.response?.data?.detail || error.message
      });
    } finally {
      setTesting(false);
    }
  };

  const parseLogOutput = (outputData) => {
    if (!outputData) return null;
    if (typeof outputData === 'object') return outputData;
    if (typeof outputData === 'string') {
      try {
        return JSON.parse(outputData);
      } catch {
        return null;
      }
    }
    return null;
  };

  const formatActionLabel = (node) => {
    if (!node) return 'Action';
    const label = node.data?.label || node.data?.actionType || node.id;
    return label;
  };

  const formatSummary = (log) => {
    const node = nodes.find(n => n.id === log.node_id);
    const nodeType = log.node_type || node?.type;
    const output = parseLogOutput(log.output_data);

    if (nodeType === 'trigger') {
      const triggerType = node?.data?.triggerType || 'trigger';
      return `Trigger fired: ${triggerType.replace(/_/g, ' ')}`;
    }

    if (nodeType === 'ifelse') {
      const matched = output?.matched_branch || output?.branch || output?.result;
      if (matched) {
        return `Condition evaluated: matched ${matched}`;
      }
      return 'Condition evaluated';
    }

    if (nodeType === 'action') {
      const actionType = node?.data?.actionType;
      if (actionType === 'update_assignees') {
        const updateInfo = output?.would_update;
        if (updateInfo) {
          const action = updateInfo.action || 'update';
          const source = updateInfo.source || 'specific users';
          const users = Array.isArray(updateInfo.users) ? updateInfo.users.join(', ') : updateInfo.users;
          return `Would ${action} assignees from ${source}${users ? ` (${users})` : ''}`;
        }
        return 'Assignees would be updated';
      }
      if (actionType === 'send_notification') {
        return 'Would send notification';
      }
      if (actionType === 'send_portal_invite') {
        return 'Would send portal invite';
      }
      if (actionType === 'grant_portal_access') {
        return 'Would grant portal access';
      }
      return `Action executed: ${formatActionLabel(node)}`;
    }

    return `Step executed: ${formatActionLabel(node)}`;
  };

  // Save workflow and return the new ID (for auto-save scenarios)
  const saveWorkflowAndReturnId = async () => {
    if (!workflowName.trim()) {
      return null; // Return null to indicate workflow name is required
    }

    if (nodes.length === 0) {
      return null;
    }

    try {
      setSaving(true);
      
      // Clean nodes - remove event handlers before sending to backend
      const cleanNodes = nodes.map(node => {
        const normalizedNode = normalizeActionNode(node);
        return ({
        id: node.id,
        type: node.type,
        position: node.position,
        data: {
          ...normalizedNode.data,
          onConfigClick: undefined,
          onDelete: undefined,
          onCopy: undefined
        }
      });
      });
      
      // Remove undefined keys from node data
      cleanNodes.forEach(node => {
        Object.keys(node.data).forEach(key => {
          if (node.data[key] === undefined) {
            delete node.data[key];
          }
        });
      });
      
      // Ensure trigger_config is an object
      let cleanTriggerConfig = triggerConfig;
      if (typeof triggerConfig === 'string') {
        try {
          cleanTriggerConfig = JSON.parse(triggerConfig);
        } catch {
          cleanTriggerConfig = {};
        }
      }
      if (!cleanTriggerConfig || typeof cleanTriggerConfig !== 'object') {
        cleanTriggerConfig = {};
      }
      
      const workflowData = {
        name: workflowName,
        description: workflowDescription,
        trigger_type: triggerType,
        trigger_config: cleanTriggerConfig,
        nodes: cleanNodes,
        edges: edges,
        enabled: true,
      };

      if (currentWorkflowId) {
        await api.put(`/workflow-automations/${currentWorkflowId}`, workflowData);
        return currentWorkflowId;
      } else {
        const response = await api.post('/workflow-automations', workflowData);
        // Update local workflow ID for subsequent saves
        // API returns { success: true, workflow: {...} }
        const newId = response.data.workflow.id;
        setCurrentWorkflowId(newId);
        return newId;
      }
    } catch (err) {
      console.error('Error saving workflow:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const showSaveToast = (type, message) => {
    setSaveToast({ type, message });
    setTimeout(() => setSaveToast(null), 2600);
  };

  const saveWorkflow = async () => {
    if (!workflowName.trim()) {
      alert('Please enter a workflow name');
      return;
    }

    if (nodes.length === 0) {
      alert('Please add at least one node to your workflow');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('access_token');
      
      // Clean nodes - remove event handlers before sending to backend
      // Spread all data to preserve custom fields like branches, elseEnabled, etc.
      const cleanNodes = nodes.map(node => {
        const normalizedNode = normalizeActionNode(node);
        return ({
        id: node.id,
        type: node.type,
        position: node.position,
        data: {
          ...normalizedNode.data,
          // Remove function references that can't be serialized
          onConfigClick: undefined,
          onDelete: undefined,
          onCopy: undefined
        }
      });
      });
      
      // Remove undefined keys from node data
      cleanNodes.forEach(node => {
        Object.keys(node.data).forEach(key => {
          if (node.data[key] === undefined) {
            delete node.data[key];
          }
        });
      });
      
      // Ensure trigger_config is an object, not a string
      let cleanTriggerConfig = triggerConfig;
      if (typeof triggerConfig === 'string') {
        try {
          cleanTriggerConfig = JSON.parse(triggerConfig);
        } catch {
          cleanTriggerConfig = {};
        }
      }
      if (!cleanTriggerConfig || typeof cleanTriggerConfig !== 'object') {
        cleanTriggerConfig = {};
      }
      
      const workflowData = {
        name: workflowName,
        description: workflowDescription,
        trigger_type: triggerType,
        trigger_config: cleanTriggerConfig,
        nodes: cleanNodes,
        edges: edges,
        enabled: true,
      };

      console.log('Saving workflow data:', JSON.stringify(workflowData, null, 2));

      if (currentWorkflowId) {
        // Update existing
        await api.put(`/workflow-automations/${currentWorkflowId}`, workflowData);
      } else {
        // Create new
        const response = await api.post('/workflow-automations', workflowData);
        // Update local workflow ID so subsequent saves work
        // API returns { success: true, workflow: {...} }
        setCurrentWorkflowId(response.data.workflow.id);
      }

      // Show brief success message - don't close, user can navigate away when ready
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
      showSaveToast('success', 'Workflow saved successfully');
    } catch (err) {
      console.error('Error saving workflow:', err);
      const message = err.response?.data?.detail || 'Failed to save workflow';
      showSaveToast('error', message);
      alert(message);
    } finally {
      setSaving(false);
    }
  };

  const onDragStart = (event, nodeType, nodeData) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.setData('application/nodedata', JSON.stringify(nodeData));
    event.dataTransfer.effectAllowed = 'move';
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {saveToast && (
        <div className="fixed top-4 right-4 z-50">
          <div className={`px-4 py-3 rounded-lg shadow-lg text-white ${saveToast.type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
            {saveToast.message}
          </div>
        </div>
      )}

      {/* Top Bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex-1 min-w-0 max-w-2xl">
              <input
                type="text"
                value={workflowName}
                onChange={(e) => setWorkflowName(e.target.value)}
                placeholder="Workflow Name"
                className="text-2xl font-bold text-gray-900 border-none outline-none bg-transparent w-full truncate"
              />
              <input
                type="text"
                value={workflowDescription}
                onChange={(e) => setWorkflowDescription(e.target.value)}
                placeholder="Add description..."
                className="text-sm text-gray-600 border-none outline-none bg-transparent w-full"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setShowTestModal(true);
                fetchTestData();
              }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
            >
              <Play className="w-4 h-4" />
              Test Workflow
            </button>
            <button
              onClick={saveWorkflow}
              disabled={saving}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${
                saveSuccess 
                  ? 'bg-green-600 text-white' 
                  : 'bg-purple-600 text-white hover:bg-purple-700'
              }`}
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : saveSuccess ? 'Saved!' : 'Save Workflow'}
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 flex">
        {/* Node Palette */}
        {showNodePalette && (
          <div className="w-72 bg-white border-r border-gray-200 p-4 overflow-y-auto">
            <h3 className="font-semibold text-gray-900 mb-4">Add Nodes</h3>
            
            {/* Search for All Nodes */}
            <div className="mb-4">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search nodes..."
                  value={actionSearch}
                  onChange={(e) => setActionSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                {actionSearch && (
                  <button
                    onClick={() => setActionSearch('')}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            
            {/* Trigger Nodes */}
            <div className="mb-4">
              <button
                onClick={() => setExpandedSections(prev => ({ ...prev, triggers: !prev.triggers }))}
                className="flex items-center justify-between w-full text-left mb-2 hover:bg-gray-50 rounded p-1 -ml-1"
              >
                <h4 className="text-xs font-semibold text-gray-500 uppercase">Triggers</h4>
                {expandedSections.triggers ? (
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                )}
              </button>
              {expandedSections.triggers && (
              <div className="space-y-2">
                {[
                  { label: 'Stage Changed', triggerType: 'task_stage_changed' },
                  { label: 'Task Created', triggerType: 'task_created' },
                  { label: 'Project Created', triggerType: 'project_created' },
                  { label: 'Client Created', triggerType: 'client_created' },
                  { label: 'Category Changed', triggerType: 'category_changed' },
                  { label: 'Task Completed', triggerType: 'task_completed' },
                  { label: 'Task Assigned', triggerType: 'task_assigned' },
                  { label: 'Priority Changed', triggerType: 'priority_changed' },
                  { label: 'Task Due Soon', triggerType: 'task_due_soon' },
                  { label: 'Task Overdue', triggerType: 'task_overdue' },
                  { label: 'Comment Added', triggerType: 'comment_added' },
                  { label: 'Portal Submission', triggerType: 'client_portal_submission' },
                  { label: 'Portal Access Granted', triggerType: 'portal_access_granted' },
                  { label: 'Inbound Webhook', triggerType: 'inbound_webhook' },
                ]
                  .filter(trigger => !actionSearch || trigger.label.toLowerCase().includes(actionSearch.toLowerCase()))
                  .map(trigger => (
                    <div
                      key={trigger.triggerType}
                      className="p-3 bg-green-50 border border-green-200 rounded-lg cursor-move hover:bg-green-100 transition-colors"
                      draggable
                      onDragStart={(e) => onDragStart(e, 'trigger', { label: trigger.label, triggerType: trigger.triggerType })}
                    >
                      <div className="flex items-center gap-2">
                        <Zap className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium text-gray-900">{trigger.label}</span>
                      </div>
                    </div>
                  ))}
              </div>
              )}
            </div>

            {/* Logic / Branching */}
            <div className="mb-4">
              <button
                onClick={() => setExpandedSections(prev => ({ ...prev, logic: !prev.logic }))}
                className="flex items-center justify-between w-full text-left mb-2 hover:bg-gray-50 rounded p-1 -ml-1"
              >
                <h4 className="text-xs font-semibold text-gray-500 uppercase">Logic</h4>
                {expandedSections.logic ? (
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                )}
              </button>
              {expandedSections.logic && (
              <div className="space-y-2">
                {(!actionSearch || 'if else branch conditional'.includes(actionSearch.toLowerCase()) || 'if / else'.toLowerCase().includes(actionSearch.toLowerCase())) && (
                <div
                  className="p-3 bg-purple-50 border border-purple-200 rounded-lg cursor-move hover:bg-purple-100 transition-colors"
                  draggable
                  onDragStart={(e) => onDragStart(e, 'ifelse', { 
                    label: 'If / Else', 
                    branches: [{ id: 'branch-1', name: 'Branch 1', conditionGroups: [] }],
                    elseEnabled: true
                  })}
                >
                  <div className="flex items-center gap-2">
                    <GitBranch className="w-4 h-4 text-purple-600" />
                    <span className="text-sm font-medium text-gray-900">If / Else</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Multi-branch conditional with AND/OR logic</p>
                </div>
                )}
              </div>
              )}
            </div>

            {/* Action Nodes */}
            <div className="mb-6">
              <button
                onClick={() => setExpandedSections(prev => ({ ...prev, actions: !prev.actions }))}
                className="flex items-center justify-between w-full text-left mb-2 hover:bg-gray-50 rounded p-1 -ml-1"
              >
                <h4 className="text-xs font-semibold text-gray-500 uppercase">Actions</h4>
                {expandedSections.actions ? (
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                )}
              </button>
              {expandedSections.actions && (
              <div className="space-y-2">
                {[
                  { label: 'Send Webhook', actionType: 'webhook', icon: Send, color: 'blue' },
                  { label: 'Send Email', actionType: 'email', icon: Mail, color: 'blue' },
                  { label: 'Send Portal Invite', actionType: 'send_portal_invite', icon: Mail, color: 'green' },
                  { label: 'Grant Portal Access', actionType: 'grant_portal_access', icon: Users, color: 'green' },
                  { label: 'AI Task Summary', actionType: 'ai_generate', icon: Sparkles, color: 'blue' },
                  { label: 'GPT Action', actionType: 'gpt_action', icon: Sparkles, color: 'blue' },
                  { label: 'Wait', actionType: 'wait', icon: Target, color: 'amber' },
                  { label: 'Go To', actionType: 'go_to', icon: Target, color: 'purple' },
                  { label: 'Add to Workflow', actionType: 'add_to_workflow', icon: Zap, color: 'green' },
                  { label: 'Remove from Workflow', actionType: 'remove_from_workflow', icon: Zap, color: 'red' },
                  { label: 'Update Assignees', actionType: 'update_assignees', icon: Target, color: 'blue' },
                  { label: 'Change Category', actionType: 'change_category', icon: Tag, color: 'blue' },
                  { label: 'Update Project Manager', actionType: 'update_project_manager', icon: Users, color: 'blue' },
                  { label: 'Create Task', actionType: 'create_task', icon: CheckSquare, color: 'blue' },
                  { label: 'Add Subtask', actionType: 'add_subtask', icon: CheckSquare, color: 'green' },
                  { label: 'Create Project', actionType: 'create_project', icon: Plus, color: 'blue' },
                  { label: 'Create Client', actionType: 'create_client', icon: Users, color: 'green' },
                  { label: 'Archive Task', actionType: 'archive_task', icon: Target, color: 'amber' },
                  { label: 'Update Priority', actionType: 'update_priority', icon: Target, color: 'blue' },
                  { label: 'Move to Stage', actionType: 'move_to_stage', icon: Target, color: 'blue' },
                  { label: 'Change Project', actionType: 'change_project', icon: Target, color: 'purple' },
                  { label: 'Set Due Date', actionType: 'set_due_date', icon: Target, color: 'blue' },
                  { label: 'Add Comment', actionType: 'add_comment', icon: Mail, color: 'blue' },
                  { label: 'Send Notification', actionType: 'send_notification', icon: Zap, color: 'blue' },
                  { label: 'Add Tag', actionType: 'add_tag', icon: Tag, color: 'blue' },
                  { label: 'Remove Tag', actionType: 'remove_tag', icon: Tag, color: 'blue' },
                ]
                  .filter(action => !actionSearch || action.label.toLowerCase().includes(actionSearch.toLowerCase()))
                  .map(action => {
                    const IconComponent = action.icon;
                    const colorClasses = {
                      blue: 'bg-blue-50 border-blue-200 hover:bg-blue-100 text-blue-600',
                      amber: 'bg-amber-50 border-amber-200 hover:bg-amber-100 text-amber-600',
                      green: 'bg-green-50 border-green-200 hover:bg-green-100 text-green-600',
                      purple: 'bg-purple-50 border-purple-200 hover:bg-purple-100 text-purple-600',
                      red: 'bg-red-50 border-red-200 hover:bg-red-100 text-red-600',
                    };
                    const classes = colorClasses[action.color] || colorClasses.blue;
                    return (
                      <div
                        key={action.actionType}
                        className={`p-3 ${classes.split(' ').slice(0, 3).join(' ')} border rounded-lg cursor-move transition-colors`}
                        draggable
                        onDragStart={(e) => onDragStart(e, 'action', { label: action.label, actionType: action.actionType })}
                      >
                        <div className="flex items-center gap-2">
                          <IconComponent className={`w-4 h-4 ${classes.split(' ').slice(-1)[0]}`} />
                          <span className="text-sm font-medium text-gray-900">{action.label}</span>
                        </div>
                      </div>
                    );
                  })}
                {actionSearch && [
                  { label: 'Send Webhook', actionType: 'webhook' },
                  { label: 'Send Email', actionType: 'email' },
                  { label: 'Send Portal Invite', actionType: 'send_portal_invite' },
                  { label: 'AI Task Summary', actionType: 'ai_generate' },
                  { label: 'GPT Action', actionType: 'gpt_action' },
                  { label: 'Wait', actionType: 'wait' },
                  { label: 'Go To', actionType: 'go_to' },
                  { label: 'Add to Workflow', actionType: 'add_to_workflow' },
                  { label: 'Remove from Workflow', actionType: 'remove_from_workflow' },
                  { label: 'Update Assignees', actionType: 'update_assignees' },
                  { label: 'Change Category', actionType: 'change_category' },
                  { label: 'Update Project Manager', actionType: 'update_project_manager' },
                  { label: 'Create Task', actionType: 'create_task' },
                  { label: 'Add Subtask', actionType: 'add_subtask' },
                  { label: 'Update Priority', actionType: 'update_priority' },
                  { label: 'Move to Stage', actionType: 'move_to_stage' },
                  { label: 'Change Project', actionType: 'change_project' },
                  { label: 'Set Due Date', actionType: 'set_due_date' },
                  { label: 'Add Comment', actionType: 'add_comment' },
                  { label: 'Send Notification', actionType: 'send_notification' },
                  { label: 'Add Tag', actionType: 'add_tag' },
                  { label: 'Remove Tag', actionType: 'remove_tag' },
                ].filter(a => a.label.toLowerCase().includes(actionSearch.toLowerCase())).length === 0 && (
                  <p className="text-xs text-gray-500 text-center py-2">No actions match &quot;{actionSearch}&quot;</p>
                )}
              </div>
              )}
            </div>
          </div>
        )}

        {/* React Flow Canvas */}
        <div className="flex-1" ref={reactFlowWrapper}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onInit={(instance) => {
              setReactFlowInstance(instance);
              // Focus on the first node (trigger) when opening an existing workflow
              if (workflow?.nodes?.length > 0) {
                setTimeout(() => {
                  // Find the trigger node or first node
                  const triggerNode = workflow.nodes.find(n => n.type === 'trigger') || workflow.nodes[0];
                  if (triggerNode?.position) {
                    // Set viewport so the first node is near the top of the canvas
                    // Position it with some padding from the top (y offset of ~50px in viewport)
                    instance.setViewport({
                      x: -triggerNode.position.x + 300, // Center horizontally with offset
                      y: -triggerNode.position.y + 80,  // Position near top with padding
                      zoom: 1
                    }, { duration: 300 });
                  } else {
                    // Fallback to fitView if no position data
                    instance.fitView({ padding: 0.2 });
                  }
                }, 100);
              }
            }}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onNodeDoubleClick={onNodeDoubleClick}
            onNodesDelete={onNodesDelete}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            defaultViewport={{ x: 0, y: 0, zoom: 1 }}
            deleteKeyCode="Delete"
          >
            <Controls />
            <MiniMap />
            <Background variant="dots" gap={12} size={1} />
          </ReactFlow>
        </div>
      </div>

      {/* Node Configuration Modal */}
      {selectedNodeForConfig && (
        <NodeConfigModal
          node={selectedNodeForConfig}
          workflowId={currentWorkflowId}
          allNodes={nodes}
          webhookPayloadVariables={webhookPayloadVariables}
          onWebhookVariablesChange={setWebhookPayloadVariables}
          onClose={() => setSelectedNodeForConfig(null)}
          onSave={handleSaveNodeConfig}
          onSaveWorkflow={saveWorkflowAndReturnId}
        />
      )}

      {/* Test Workflow Modal */}
      {showTestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-blue-50 to-purple-50">
              <div className="flex items-center gap-2">
                <Play className="w-5 h-5 text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-900">Test Workflow</h3>
              </div>
              <button onClick={() => {
                setShowTestModal(false);
                setTestResults(null);
              }} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <p className="text-sm text-gray-600">
                Test your workflow with real data before enabling it. Select a task and optionally a client to simulate the workflow execution.
              </p>

              {/* Task Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Task <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedTestTask}
                  onChange={(e) => setSelectedTestTask(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Choose a task...</option>
                  {testTasks.map(task => (
                    <option key={task.id} value={task.id}>
                      {task.name} - {task.status} ({task.category_name || 'No Category'})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {testTasks.length === 0 ? 'Loading tasks...' : `${testTasks.length} tasks available`}
                </p>
              </div>

              {/* Client Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Client <span className="text-gray-500">(optional)</span>
                </label>
                <select
                  value={selectedTestClient}
                  onChange={(e) => setSelectedTestClient(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">No client (will use task&apos;s client if available)</option>
                  {testClients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.name} {client.email ? `(${client.email})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* User Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Team User <span className="text-gray-500">(optional)</span>
                </label>
                <select
                  value={selectedTestUser}
                  onChange={(e) => setSelectedTestUser(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">No user (will use task&apos;s user if available)</option>
                  {testUsers.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name} ({user.email})
                    </option>
                  ))}
                </select>
              </div>

              {/* Test Results */}
              {testResults && (
                <div className={`p-4 rounded-lg border-2 ${testResults.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  <div className="flex items-start gap-2 mb-3">
                    {testResults.success ? (
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <h4 className={`font-semibold ${testResults.success ? 'text-green-900' : 'text-red-900'}`}>
                        {testResults.success ? 'Test Successful!' : 'Test Failed'}
                      </h4>
                      {testResults.error && (
                        <p className="text-sm text-red-700 mt-1">{testResults.error}</p>
                      )}
                    </div>
                  </div>

                  {testResults.execution && (
                    <div className="space-y-2 mt-3">
                      <div className="text-sm">
                        <strong>Status:</strong> {testResults.execution.status}
                      </div>
                      {testResults.logs && testResults.logs.length > 0 && (
                        <div className="mt-3">
                          <strong className="text-sm">Summary:</strong>
                          <div className="mt-2 space-y-2 max-h-60 overflow-y-auto">
                            {testResults.logs.map((log, idx) => (
                              <div key={idx} className="flex items-start gap-2 text-xs bg-white bg-opacity-70 p-2 rounded">
                                {log.status === 'success' ? (
                                  <CheckCircle className="w-3 h-3 text-green-600 flex-shrink-0 mt-0.5" />
                                ) : log.status === 'failed' ? (
                                  <XCircle className="w-3 h-3 text-red-600 flex-shrink-0 mt-0.5" />
                                ) : (
                                  <AlertCircle className="w-3 h-3 text-yellow-600 flex-shrink-0 mt-0.5" />
                                )}
                                <div className="flex-1">
                                  <div className="font-medium">{formatSummary(log)}</div>
                                  {log.error_message && (
                                    <div className="text-red-700 mt-1">{log.error_message}</div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>

                          <div className="mt-3">
                            <button
                              type="button"
                              onClick={() => setShowRawTestLog((prev) => !prev)}
                              className="text-xs font-medium text-blue-700 hover:text-blue-900"
                            >
                              {showRawTestLog ? 'Hide raw log' : 'View raw log'}
                            </button>
                          </div>

                          {showRawTestLog && (
                            <div className="mt-2 space-y-1 max-h-60 overflow-y-auto">
                              {testResults.logs.map((log, idx) => (
                                <div key={`raw-${idx}`} className="flex items-start gap-2 text-xs bg-white bg-opacity-50 p-2 rounded">
                                  {log.status === 'success' ? (
                                    <CheckCircle className="w-3 h-3 text-green-600 flex-shrink-0 mt-0.5" />
                                  ) : log.status === 'failed' ? (
                                    <XCircle className="w-3 h-3 text-red-600 flex-shrink-0 mt-0.5" />
                                  ) : (
                                    <AlertCircle className="w-3 h-3 text-yellow-600 flex-shrink-0 mt-0.5" />
                                  )}
                                  <div className="flex-1">
                                    <div className="font-medium">{log.node_id}</div>
                                    {log.log_message && <div className="text-gray-600">{log.log_message}</div>}
                                    {log.output_data && (
                                      <pre className="text-xs text-gray-500 mt-1 overflow-x-auto">
                                        {JSON.stringify(log.output_data, null, 2)}
                                      </pre>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-2 bg-gray-50">
              <button
                onClick={() => {
                  setShowTestModal(false);
                  setTestResults(null);
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Close
              </button>
              <button
                onClick={runTestWorkflow}
                disabled={testing || !selectedTestTask}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {testing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Testing...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4" />
                    Run Test
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowBuilder;
